/**
 * Storage Abstraction Layer
 *
 * Provides a unified storage interface that works across platforms:
 * - Web: Uses localStorage
 * - React Native: Uses injected storage adapter (e.g., AsyncStorage wrapper)
 */

interface StorageAdapter {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

class UniversalStorage implements StorageAdapter {
  private adapter: StorageAdapter | null = null

  setAdapter(adapter: StorageAdapter) {
    this.adapter = adapter
  }

  private getStorage(): StorageAdapter {
    if (this.adapter) {
      return this.adapter
    }

    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage
    }

    console.warn(
      'No storage adapter available. Using in-memory storage. Data will not persist.'
    )
    return this.memoryStorage
  }

  private memoryStorage: StorageAdapter = (() => {
    const store = new Map<string, string>()
    return {
      getItem: (key: string) => store.get(key) ?? null,
      setItem: (key: string, value: string) => {
        store.set(key, value)
      },
      removeItem: (key: string) => {
        store.delete(key)
      },
    }
  })()

  getItem(key: string): string | null {
    try {
      return this.getStorage().getItem(key)
    } catch (error) {
      console.error(`Storage getItem error for key "${key}":`, error)
      return null
    }
  }

  setItem(key: string, value: string): void {
    try {
      this.getStorage().setItem(key, value)
    } catch (error) {
      console.error(`Storage setItem error for key "${key}":`, error)
    }
  }

  removeItem(key: string): void {
    try {
      this.getStorage().removeItem(key)
    } catch (error) {
      console.error(`Storage removeItem error for key "${key}":`, error)
    }
  }
}

export const storage = new UniversalStorage()

export type { StorageAdapter }
